import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { dayKey } from '../utils/score';

// Data Types
export interface User {
  uid: string;
  email: string;
  displayName: string;
  avatarEmoji?: string;
  subscriptionTier?: string;
  accountCreatedAt: string;
  connections: string[];
  pushToken: string | null;
  timezone: string;
  timezoneUpdatedAt: Date;
  accountDisabled: boolean;
  createdAt: string;
  updatedAt: string;
  customCategories?: { name: string; createdAt: string }[];
  hiddenDefaultCategories?: string[];
  senseiPreferences?: string;
}

export interface Habit {
  id: string;
  userId: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  reminder: string | null;
  startTime: string | null;
  endTime: string | null;
  reminderSent: boolean;
  lastReminderSent: Date | null;
  streak: number;
  bestStreak: number;
  lastCompleted: string | null;
  order: number;
  createdAt: any;
  updatedAt: any;
}

export interface Task {
  id: string;
  title: string;
  notes: string;
  category: string;
  color: string;
  tag?: string | null;
  tags?: string[];
  ref: string | null;
  deadline: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  reminder: string | null;
  reminderSent: boolean;
  status: 'not_started' | 'in_progress' | 'paused' | 'completed' | 'draft' | 'declined' | 'pending' | 'accepted';
  duration: number;
  actualDuration: number;
  userId: string;
  createdBy: string;
  assignedTo: string;
  watchers: string[];
  dependencies: string[];
  ganttChartIds: string[];
  isRecurring: boolean;
  isPrivate: boolean;
  completed: boolean;
  progress: number;
  links: string[];
  extensions: any[];
  comments: any[];
  createdAt: any;
  startedAt: any | null;
  completedAt: any | null;
  updatedAt: any;
}

export interface List {
  id: string;
  name: string;
  owner: string;
  ownerEmail: string;
  members: string[];
  color: [string, string];
  icon: string;
  itemCount: number;
  checkedCount: number;
  createdAt: any;
  updatedAt: any;
}


export interface Feeling {
  id: string;
  feeling: string;
  intensity: number;
  note: string;
  timestamp: string;
  date: string;
  createdAt: any;
  updatedAt: any;
}

export interface BudgetCategory {
  id: string;
  userId: string;
  name: string;
  budgetAmount: number;
  color: string;
  icon: string;
  createdAt: any;
  updatedAt: any;
}

export interface BudgetEntry {
  id: string;
  userId: string;
  categoryId: string;
  description: string;
  amount: number;
  date: string;
  createdAt: any;
  updatedAt: any;
}

export interface BudgetSettings {
  userId: string;
  overallBudget: number;
  currency: string;
  updatedAt: any;
}

// Constants
export const DEFAULT_CATEGORIES = [
  'General', 'Work', 'Personal', 'Health', 'Learning', 'Finance', 'Other'
];

export const TASK_COLORS = [
  { name: 'Purple', hex: '#9333EA' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Green', hex: '#10B981' },
  { name: 'Orange', hex: '#F59E0B' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Indigo', hex: '#6366F1' }
];

export const HABIT_GRADIENTS = [
  ['#667eea', '#764ba2'], ['#f093fb', '#f5576c'], ['#4facfe', '#00f2fe'], 
  ['#43e97b', '#38f9d7'], ['#fa709a', '#fee140'], ['#30cfd0', '#330867'],
  ['#FF6B6B', '#4ECDC4'], ['#FD297B', '#FF5864'], ['#9F44D3', '#E55D87'],
  ['#00D2FF', '#3A7BD5'], ['#FE8C00', '#F83600'], ['#38EF7D', '#11998E']
];

/**
 * The mood palette.
 *
 * Ids, labels, emoji and sentiment are the contract -- a mood document stores
 * only the id, so both platforms must agree on what 'happy' means. The colours
 * are presentation, but they were diverging: the web still had the bright
 * pre-rebrand values (#4ADE80, #FCD34D, #60A5FA, …) while the phone moved to
 * the dojo palette, so the same logged mood was green here and a different
 * green there. These are the phone's values, from MoodContext.MOOD_OPTIONS.
 */
export const MOOD_OPTIONS = [
  { id: 'happy', emoji: '😊', label: 'Happy', color: '#5FA83A', sentiment: 'positive' },
  { id: 'energetic', emoji: '⚡', label: 'Energetic', color: '#E8B33A', sentiment: 'positive' },
  { id: 'calm', emoji: '😌', label: 'Calm', color: '#6E93C9', sentiment: 'positive' },
  { id: 'motivated', emoji: '💪', label: 'Motivated', color: '#C1763C', sentiment: 'positive' },
  { id: 'excited', emoji: '🤩', label: 'Excited', color: '#8E5572', sentiment: 'positive' },
  { id: 'focused', emoji: '🎯', label: 'Focused', color: '#8E5572', sentiment: 'positive' },
  { id: 'anxious', emoji: '😰', label: 'Anxious', color: '#E36A5E', sentiment: 'negative' },
  { id: 'sad', emoji: '😢', label: 'Sad', color: '#6E93C9', sentiment: 'negative' },
  { id: 'tired', emoji: '😴', label: 'Tired', color: '#C7D2FE', sentiment: 'negative' },
  { id: 'stressed', emoji: '😤', label: 'Stressed', color: '#FDBA74', sentiment: 'negative' },
];

// Utility Functions
export const getDateString = (date: Date): string => {
  return dayKey(date); // YYYY-MM-DD format
};

export const generateId = (prefix: string, userId?: string): string => {
  const timestamp = Date.now();
  const userIdPrefix = userId ? userId.substring(0, 8) : '';
  return `${prefix}_${timestamp}${userIdPrefix ? '_' + userIdPrefix : ''}`;
};

// User Services
export const getUserProfile = async (uid: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const createUserProfile = async (userData: Partial<User>): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userData.uid!);
    // Safety check: never overwrite an existing profile
    const existing = await getDoc(userRef);
    if (existing.exists()) {
      console.log('User profile already exists, skipping creation');
      return;
    }
    await setDoc(userRef, {
      ...userData,
      accountCreatedAt: new Date().toISOString(),
      connections: [],
      pushToken: null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneUpdatedAt: new Date(),
      accountDisabled: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, updates: Partial<User>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Category management
export const renameCategory = async (
  uid: string,
  oldName: string,
  newName: string,
  user: User,
  tasks: Task[]
): Promise<void> => {
  // Update all tasks that use this category
  const affectedTasks = tasks.filter((t) => t.category === oldName);
  await Promise.all(affectedTasks.map((t) => updateTask(t.id, { category: newName })));

  // Update customCategories on user profile if it's a custom one
  const customs = user.customCategories || [];
  const idx = customs.findIndex((c) => c.name === oldName);
  if (idx !== -1) {
    const updated = [...customs];
    updated[idx] = { ...updated[idx], name: newName };
    await updateUserProfile(uid, { customCategories: updated });
  }
};

export const deleteCategoryForUser = async (
  uid: string,
  categoryName: string,
  user: User
): Promise<void> => {
  const isDefault = DEFAULT_CATEGORIES.includes(categoryName);

  if (isDefault) {
    // Hide the default category
    const hidden = user.hiddenDefaultCategories || [];
    if (!hidden.includes(categoryName)) {
      await updateUserProfile(uid, { hiddenDefaultCategories: [...hidden, categoryName] });
    }
  } else {
    // Remove from customCategories
    const customs = user.customCategories || [];
    await updateUserProfile(uid, {
      customCategories: customs.filter((c) => c.name !== categoryName),
    });
  }
};

// Habit Services
export const getHabits = (userId: string, callback: (habits: Habit[]) => void) => {
  const tryQuery = (useOrderBy: boolean) => {
    const constraints = [
      where('userId', '==', userId),
      ...(useOrderBy ? [orderBy('order')] : []),
    ];
    const habitsQuery = query(collection(db, 'habits'), ...constraints);

    return onSnapshot(habitsQuery, (snapshot) => {
      const habits = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Habit));
      if (!useOrderBy) {
        habits.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      }
      callback(habits);
    }, (error) => {
      console.error('Error fetching habits:', error);
      if (useOrderBy) {
        console.log('Retrying habits query without orderBy...');
        unsub = tryQuery(false);
      }
    });
  };

  let unsub = tryQuery(true);
  return () => unsub();
};

export const createHabit = async (habitData: Partial<Habit>, userId: string): Promise<string> => {
  try {
    const habitId = generateId('habit', userId);
    const newHabit = {
      ...habitData,
      id: habitId,
      userId,
      streak: 0,
      bestStreak: 0,
      lastCompleted: null,
      reminderSent: false,
      lastReminderSent: null,
      // Sequential, matching taskdojo-app's HabitsContext.createHabit. It must
      // not be Date.now(): the phone writes small integers, so an epoch here
      // sorts every web-created habit permanently below every phone-created
      // one. The caller's value wins, which is why the spread comes after.
      order: 0,
      ...habitData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'habits', habitId), newHabit);
    return habitId;
  } catch (error) {
    console.error('Error creating habit:', error);
    throw error;
  }
};

export const updateHabit = async (habitId: string, updates: Partial<Habit>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'habits', habitId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating habit:', error);
    throw error;
  }
};

export const deleteHabit = async (habitId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'habits', habitId));
  } catch (error) {
    console.error('Error deleting habit:', error);
    throw error;
  }
};

// Task Services — 3 user-scoped queries (matches iOS approach)
export const getTasks = (userId: string, callback: (tasks: Task[]) => void) => {
  const tasksRef = collection(db, 'tasks');

  // Query 1: Tasks created by user
  const createdQuery = query(tasksRef, where('createdBy', '==', userId));
  // Query 2: Tasks assigned to user
  const assignedQuery = query(tasksRef, where('assignedTo', '==', userId));
  // Query 3: Tasks user is watching
  const watchingQuery = query(tasksRef, where('watchers', 'array-contains', userId));

  const results: Record<string, Task> = {};
  let listenersReady = 0;

  const mergeAndCallback = () => {
    const merged = Object.values(results).sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime.getTime() - aTime.getTime();
    });
    callback(merged);
  };

  const processSnapshot = (snapshot: any) => {
    snapshot.docs.forEach((doc: any) => {
      results[doc.id] = { id: doc.id, ...doc.data() } as Task;
    });
    // Also remove docs that were deleted
    snapshot.docChanges().forEach((change: any) => {
      if (change.type === 'removed') {
        delete results[change.doc.id];
      }
    });
    listenersReady++;
    if (listenersReady >= 3) {
      mergeAndCallback();
    }
  };

  const handleError = (error: any) => {
    console.error('Error fetching tasks:', error);
    listenersReady++;
    if (listenersReady >= 3) {
      mergeAndCallback();
    }
  };

  const unsub1 = onSnapshot(createdQuery, processSnapshot, handleError);
  const unsub2 = onSnapshot(assignedQuery, processSnapshot, handleError);
  const unsub3 = onSnapshot(watchingQuery, processSnapshot, handleError);

  return () => {
    unsub1();
    unsub2();
    unsub3();
  };
};

export const createTask = async (taskData: Partial<Task>, userId: string): Promise<string> => {
  try {
    const taskId = generateId('task', userId);

    // Field for field with taskdojo-app's TaskContext.createTask. Both
    // platforms write to the same collection, so a task created here has to be
    // indistinguishable from one created on the phone -- a field the phone
    // writes and the web omits is a field the phone later reads as undefined.
    //
    // Defaults sit before the spread so a caller can override them; anything
    // the phone derives rather than accepts sits after it.
    const newTask = {
      notes: '',
      category: 'General',
      color: TASK_COLORS[0].hex,
      deadline: null,
      reminder: null,
      progress: 0,
      duration: 0,
      links: [],
      ...taskData,
      id: taskId,
      userId,
      createdBy: userId,
      assignedTo: taskData.assignedTo || userId,
      status: taskData.assignedTo && taskData.assignedTo !== userId ? 'pending' as const : 'not_started' as const,
      completed: false,
      actualDuration: 0,
      tag: taskData.tag || null,
      ref: taskData.ref || null,
      watchers: taskData.watchers || [],
      dependencies: taskData.dependencies || [],
      ganttChartIds: taskData.ganttChartIds || [],
      startDateTime: taskData.startDateTime || null,
      endDateTime: taskData.endDateTime || null,
      isRecurring: false,
      isPrivate: taskData.isPrivate || false,
      extensions: [],
      comments: [],
      reminderSent: false,
      startedAt: null,
      completedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'tasks', taskId), newTask);
    return taskId;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<void> => {
  try {
    const updateData: any = {
      ...updates,
      // Who made the change. The phone writes this on every task update and
      // the Cloud Functions read it to attribute the notification --
      // `after.updatedBy || after.createdBy` -- so omitting it does not drop
      // the notification, it credits the change to whoever created the task.
      // firestore.rules also names updatedBy in the watcher self-removal rule,
      // so it is an expected part of a task write, not an extra.
      updatedBy: auth.currentUser?.uid,
      updatedAt: serverTimestamp()
    };
    
    // Handle completion — only override status if status wasn't explicitly provided
    if (updates.completed !== undefined) {
      if (updates.status === undefined) {
        updateData.status = updates.completed ? 'completed' : 'not_started';
      }
      updateData.completedAt = updates.completed ? serverTimestamp() : null;
    }
    
    await updateDoc(doc(db, 'tasks', taskId), updateData);
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'tasks', taskId));
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// Task Comment Services
export interface TaskComment {
  id: string;
  userId: string;
  text: string;
  mentions: string[];
  createdAt: any;
}

export const addTaskComment = async (
  taskId: string,
  userId: string,
  text: string,
  mentions: string[] = []
): Promise<string> => {
  try {
    const commentsRef = collection(db, 'tasks', taskId, 'comments');
    const docRef = await addDoc(commentsRef, {
      userId,
      text,
      mentions,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding task comment:', error);
    throw error;
  }
};

export const subscribeToTaskComments = (
  taskId: string,
  callback: (comments: TaskComment[]) => void
) => {
  const commentsRef = collection(db, 'tasks', taskId, 'comments');
  const commentsQuery = query(commentsRef, orderBy('createdAt', 'asc'));

  return onSnapshot(
    commentsQuery,
    (snapshot) => {
      const comments: TaskComment[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      } as TaskComment));
      callback(comments);
    },
    (error) => {
      console.error('Error fetching task comments:', error);
      callback([]);
    }
  );
};

// Budget Category Services
export const createBudgetCategory = async (
  userId: string,
  data: { name: string; budgetAmount: number; color: string; icon: string }
): Promise<string> => {
  try {
    const categoryId = `budgetCat_${Date.now()}_${userId.substring(0, 8)}`;
    await setDoc(doc(db, 'budgetCategories', categoryId), {
      id: categoryId,
      userId,
      name: data.name,
      budgetAmount: data.budgetAmount,
      color: data.color,
      icon: data.icon,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return categoryId;
  } catch (error) {
    console.error('Error creating budget category:', error);
    throw error;
  }
};

export const updateBudgetCategory = async (
  id: string,
  data: Partial<BudgetCategory>
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'budgetCategories', id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating budget category:', error);
    throw error;
  }
};

export const deleteBudgetCategory = async (id: string): Promise<void> => {
  try {
    // Delete all entries with this categoryId
    const entriesQuery = query(
      collection(db, 'budgetEntries'),
      where('categoryId', '==', id)
    );
    const entriesSnapshot = await getDocs(entriesQuery);
    const deletePromises: Promise<void>[] = [];
    entriesSnapshot.forEach((docSnap) => {
      deletePromises.push(deleteDoc(docSnap.ref));
    });
    await Promise.all(deletePromises);

    // Delete the category itself
    await deleteDoc(doc(db, 'budgetCategories', id));
  } catch (error) {
    console.error('Error deleting budget category:', error);
    throw error;
  }
};

export const addBudgetEntry = async (
  userId: string,
  data: {
    categoryId: string; description: string; amount: number; date: string;
    note?: string; ref?: string;
  },
): Promise<string> => {
  try {
    const entryId = `budgetEntry_${Date.now()}_${userId.substring(0, 8)}`;
    await setDoc(doc(db, 'budgetEntries', entryId), {
      id: entryId,
      userId,
      categoryId: data.categoryId,
      description: data.description,
      amount: data.amount,
      date: data.date,
      // The phone writes both, defaulting to '', and reads them back on its
      // entry rows. Omitting them here leaves the phone reading undefined.
      note: data.note || '',
      ref: data.ref || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return entryId;
  } catch (error) {
    console.error('Error adding budget entry:', error);
    throw error;
  }
};

export const updateBudgetEntry = async (
  id: string,
  data: Partial<BudgetEntry>
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'budgetEntries', id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating budget entry:', error);
    throw error;
  }
};

export const deleteBudgetEntry = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'budgetEntries', id));
  } catch (error) {
    console.error('Error deleting budget entry:', error);
    throw error;
  }
};

export const subscribeToBudgetCategories = (
  userId: string,
  callback: (categories: BudgetCategory[]) => void
) => {
  const q = query(
    collection(db, 'budgetCategories'),
    where('userId', '==', userId)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const categories: BudgetCategory[] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as BudgetCategory));
      // Sort by creation date ascending
      categories.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateA.getTime() - dateB.getTime();
      });
      callback(categories);
    },
    (error) => {
      console.error('Error subscribing to budget categories:', error);
      callback([]);
    }
  );
};

export const subscribeToBudgetEntries = (
  userId: string,
  callback: (entries: BudgetEntry[]) => void
) => {
  const q = query(
    collection(db, 'budgetEntries'),
    where('userId', '==', userId)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const entries: BudgetEntry[] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as BudgetEntry));
      // Sort by date descending
      entries.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateB.getTime() - dateA.getTime();
      });
      callback(entries);
    },
    (error) => {
      console.error('Error subscribing to budget entries:', error);
      callback([]);
    }
  );
};

// Budget Settings Services
export const getBudgetSettings = async (userId: string): Promise<BudgetSettings | null> => {
  try {
    const settingsDoc = await getDoc(doc(db, 'budgetSettings', userId));
    if (settingsDoc.exists()) {
      return settingsDoc.data() as BudgetSettings;
    }
    return null;
  } catch (error) {
    console.error('Error fetching budget settings:', error);
    return null;
  }
};

export const updateBudgetSettings = async (
  userId: string,
  data: Partial<BudgetSettings>
): Promise<void> => {
  try {
    await setDoc(doc(db, 'budgetSettings', userId), {
      ...data,
      userId,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error updating budget settings:', error);
    throw error;
  }
};

// Export all services
export default {
  getUserProfile,
  createUserProfile,
  updateUserProfile,
  getHabits,
  createHabit,
  updateHabit,
  deleteHabit,
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  addTaskComment,
  subscribeToTaskComments,
  createBudgetCategory,
  updateBudgetCategory,
  deleteBudgetCategory,
  addBudgetEntry,
  updateBudgetEntry,
  deleteBudgetEntry,
  subscribeToBudgetCategories,
  subscribeToBudgetEntries,
  getBudgetSettings,
  updateBudgetSettings,
  getDateString,
  generateId
};